import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Download, Video } from 'lucide-react';
import TopMenu from '../../components/TopMenu';
import StatusBadge from '../../components/MockInterview/StatusBadge';
import { useNotification } from '../../notification/NotificationContext';
import {
  adminGetInterviewers,
  getAvailableSlots,
  bookInterview,
  getMyRequests,
  confirmReschedule,
  extractRoomId,
} from '../../services/mockInterviewService';
import '../../components/MockInterview/MockInterview.css';

const fmtSlot = (iso) => {
  if (!iso) return { time: '—', day: '' };
  try {
    const d = new Date(iso);
    const time = d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit', minute: '2-digit',
    });
    const day = d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      weekday: 'short', month: 'short', day: 'numeric',
    });
    return { time: `${time} IST`, day };
  } catch {
    return { time: iso, day: '' };
  }
};

const fmtCompact = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }) + ' IST';
  } catch { return iso; }
};

const getSavedNotes = (meetingLink) => {
  const roomId = meetingLink ? extractRoomId(meetingLink) : null;
  if (!roomId) return { roomId: null, notes: '' };
  const notes = localStorage.getItem(`meeting-notes-${roomId}`) || '';
  return { roomId, notes };
};

const downloadNotes = (requestId, meetingLink) => {
  const { roomId, notes } = getSavedNotes(meetingLink);
  if (!notes) return;
  const blob = new Blob([notes], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `interview-notes-${requestId || roomId}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const BookInterviewPage = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const dark = theme === 'dark';

  const [interviewers, setInterviewers] = useState([]);
  const [selectedInterviewer, setSelectedInterviewer] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const loadRequests = async () => {
    try {
      const data = await getMyRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (e) {
      showNotification(e.message || 'Failed to load requests', 'error');
    }
  };

  useEffect(() => {
    Promise.all([
      adminGetInterviewers().catch(() => []),
      getMyRequests().catch(() => []),
    ])
      .then(([list, reqs]) => {
        const active = (Array.isArray(list) ? list : []).filter((i) => i.isActive);
        setInterviewers(active);
        setRequests(Array.isArray(reqs) ? reqs : []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedInterviewer) { setSlots([]); return; }
    setLoadingSlots(true);
    setSelectedSlot('');
    getAvailableSlots(selectedInterviewer)
      .then((s) => setSlots(Array.isArray(s) ? s : []))
      .catch((e) => showNotification(e.message || 'Failed to load slots', 'error'))
      .finally(() => setLoadingSlots(false));
  }, [selectedInterviewer, showNotification]);

  const handleBook = async () => {
    if (!selectedInterviewer || !selectedSlot) {
      showNotification('Pick an interviewer and a slot', 'warning');
      return;
    }
    setBooking(true);
    try {
      await bookInterview({
        interviewerId: Number(selectedInterviewer),
        requestedSlot: new Date(selectedSlot).toISOString(),
      });
      showNotification('Interview booked successfully!', 'success');
      setSelectedSlot('');
      await loadRequests();
      const refreshed = await getAvailableSlots(selectedInterviewer);
      setSlots(Array.isArray(refreshed) ? refreshed : []);
    } catch (e) {
      showNotification(e.message || 'Booking failed', 'error');
    } finally {
      setBooking(false);
    }
  };

  const handleRescheduleResponse = async (requestId, confirmed) => {
    try {
      await confirmReschedule({ requestId, confirmed });
      showNotification(confirmed ? 'Reschedule accepted' : 'Reschedule declined', 'success');
      await loadRequests();
    } catch (e) {
      showNotification(e.message || 'Failed to respond', 'error');
    }
  };

  if (loading) {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-loading"><div className="mi-spin" /></div>
      </div>
    );
  }

  const activeCount = interviewers.length;
  const openSlotCount = slots.filter((s) => !s.isBooked).length;

  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="mi-ed">

        <div className="mi-ed-strip">
          <div className="mi-ed-strip-l">
            <span className="mi-ed-issue">№ 03 — Book 1-on-1</span>
            <span>{activeCount} interviewer{activeCount === 1 ? '' : 's'} on call</span>
            <span>All times IST</span>
          </div>
          <button className="mi-ed-back" onClick={() => navigate('/mock-interview')}>
            <ArrowLeft size={12} />
            Studio
          </button>
        </div>

        <div className="mi-ed-pagehead">
          <h1>
            Pick a <em>person</em>.<br />
            Pick a <em>time</em>.
            <svg
              className="mi-ed-scribble"
              viewBox="0 0 220 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M3 8 C 50 2, 110 11, 165 5 S 210 9, 217 4" />
            </svg>
          </h1>
          <p>
            Two steps. Find an interviewer whose stack lines up with yours,
            grab one of their open slots, and we&rsquo;ll handle the calendar
            ping. Feedback lands in your archive after the call.
          </p>
        </div>

        <div className="mi-ed-panel">
          {/* Step 1 — choose interviewer */}
          <div className="mi-ed-step">
            <span className="mi-ed-step-num">01</span>
            <span className="mi-ed-step-title">Choose an interviewer</span>
            <span className="mi-ed-step-hint">— matched to your area</span>
          </div>
          <label className="mi-ed-label">Interviewer</label>
          <select
            className="mi-ed-select"
            value={selectedInterviewer}
            onChange={(e) => setSelectedInterviewer(e.target.value)}
          >
            <option value="">— Choose someone to talk to —</option>
            {interviewers.map((i) => (
              <option key={i.id} value={i.id}>
                {i.displayName || i.email || i.userId} — {i.expertise}
              </option>
            ))}
          </select>
          {interviewers.length === 0 && (
            <p className="mi-ed-help">No active interviewers right now. Try again in a bit.</p>
          )}

          {/* Step 2 — pick slot */}
          {selectedInterviewer && (
            <>
              <div className="mi-ed-step">
                <span className="mi-ed-step-num">02</span>
                <span className="mi-ed-step-title">Pick a slot</span>
                <span className="mi-ed-step-hint">
                  — {loadingSlots
                    ? 'fetching availability…'
                    : `${openSlotCount} open · ${slots.length - openSlotCount} taken`}
                </span>
              </div>

              {!loadingSlots && slots.length === 0 && (
                <div className="mi-ed-empty-fancy">
                  <h4>No <em>open slots</em> yet.</h4>
                  <p>This interviewer hasn&rsquo;t published times — try another.</p>
                </div>
              )}

              {slots.length > 0 && (
                <div className="mi-slot-grid" style={{ marginTop: 14 }}>
                  {slots.map((s) => {
                    const { time, day } = fmtSlot(s.slotDateTime);
                    const isOn = selectedSlot === s.slotDateTime;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`mi-ed-slot ${isOn ? 'mi-ed-slot-on' : ''}`}
                        disabled={s.isBooked}
                        onClick={() => setSelectedSlot(s.slotDateTime)}
                      >
                        <span className="mi-ed-slot-day">{day}</span>
                        <span className="mi-ed-slot-time">{time}</span>
                        {s.isBooked && <span className="mi-ed-slot-flag">Taken</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mi-row mi-row-between" style={{ marginTop: 22, flexWrap: 'wrap', gap: 10 }}>
                <span className="mi-ed-help" style={{ marginTop: 0 }}>
                  {selectedSlot
                    ? `Locked in for ${fmtCompact(selectedSlot)}.`
                    : 'No slot selected yet.'}
                </span>
                <button
                  className="mi-ed-go"
                  disabled={!selectedSlot || booking}
                  onClick={handleBook}
                >
                  <Calendar size={16} />
                  {booking ? 'Booking…' : 'Lock it in'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Your requests ──────────────────────────────── */}
        <div className="mi-ed-colhead" style={{ marginTop: 40 }}>
          <span>Your requests</span>
          <em>history of asks</em>
        </div>

        {requests.length === 0 && (
          <div className="mi-ed-empty-fancy">
            <h4>Nothing on your <em>calendar</em> yet.</h4>
            <p>Once you lock in a slot, it&rsquo;ll show up here with the join link.</p>
          </div>
        )}

        {requests.length > 0 && (
          <div className="mi-ed-list">
            {requests.map((r) => {
              const hasNotes = !!getSavedNotes(r.meetingLink).notes;
              const canJoin = r.meetingLink && r.status === 'Confirmed';
              return (
                <div key={r.id} className="mi-ed-row">
                  <div>
                    <span className="mi-ed-row-cell-lbl">Requested</span>
                    <span className="mi-ed-row-cell-val">{fmtCompact(r.requestedSlot)}</span>
                  </div>
                  <div>
                    <span className="mi-ed-row-cell-lbl">Rescheduled to</span>
                    <span className="mi-ed-row-cell-val">
                      {r.rescheduledSlot ? fmtCompact(r.rescheduledSlot) : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="mi-ed-row-cell-lbl">Status</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mi-row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {canJoin && (
                      <button
                        className="mi-btn mi-btn-sm"
                        style={{ background: 'rgba(59,130,246,0.15)', borderColor: 'rgba(59,130,246,0.35)', color: '#60a5fa' }}
                        onClick={() => {
                          const roomId = extractRoomId(r.meetingLink);
                          if (roomId) navigate(`/meeting/${roomId}`);
                        }}
                      >
                        <Video size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Join
                      </button>
                    )}
                    {hasNotes && (
                      <button
                        className="mi-btn mi-btn-sm"
                        style={{ background: 'rgba(16,185,129,0.15)', borderColor: 'rgba(16,185,129,0.35)', color: '#10b981' }}
                        onClick={() => downloadNotes(r.id, r.meetingLink)}
                        title="Download notes saved during this meeting"
                      >
                        <Download size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Notes
                      </button>
                    )}
                    {r.status === 'Rescheduled' && (
                      <>
                        <button className="mi-btn mi-btn-sm mi-btn-success" onClick={() => handleRescheduleResponse(r.id, true)}>
                          Accept
                        </button>
                        <button className="mi-btn mi-btn-sm mi-btn-danger" onClick={() => handleRescheduleResponse(r.id, false)}>
                          Decline
                        </button>
                      </>
                    )}
                    {!canJoin && !hasNotes && r.status !== 'Rescheduled' && (
                      <span style={{ opacity: 0.5, fontSize: 12 }}>—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookInterviewPage;
